import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, MessageCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_STORAGE_KEY = 'prof_scaly_session_id';
const MASCOT_URL =
  'https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png';

// Simple markdown renderer for **bold**
const renderContent = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? (
      <strong key={i} className="text-emerald-700">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{p}</span>
    )
  );
};

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, sid);
  }
  return sid;
};

const ProfScalyChatBubble = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [quota, setQuota] = useState(null); // { unlimited, limit, used, remaining }
  const [limitReached, setLimitReached] = useState(false);
  const [showSignupNudge, setShowSignupNudge] = useState(false);
  const NUDGE_AFTER_MESSAGES = 15;
  const NUDGE_STORAGE_KEY = 'prof_scaly_nudge_dismissed';
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Load quota state whenever the bubble opens or auth state changes
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(
          `${API}/chat/prof-scaly/status?session_id=${sessionId.current}`,
          { headers }
        );
        setQuota(data);
        if (!data.unlimited && data.remaining <= 0) setLimitReached(true);
      } catch (e) {
        /* silent */
      }
    };
    if (isOpen) fetchStatus();
  }, [isOpen, user]);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming || limitReached) return;

    const newHistory = [...messages, { role: 'user', content: text }];
    setMessages(newHistory);
    setInput('');
    setStreaming(true);

    // Append an empty assistant message that we'll fill via streaming
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/chat/prof-scaly`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: sessionId.current,
          messages: newHistory
        })
      });

      if (response.status === 402) {
        // Guest limit reached
        setLimitReached(true);
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: 'assistant',
            content: `You've used all ${quota?.limit || 5} free messages for today. Create a free account to keep chatting with me! 🐢`
          };
          return copy;
        });
        setStreaming(false);
        return;
      }

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.content) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  role: 'assistant',
                  content: (copy[copy.length - 1].content || '') + parsed.content
                };
                return copy;
              });
            } else if (parsed.meta) {
              setQuota((prev) => ({
                ...(prev || {}),
                used: parsed.meta.messages_used,
                limit: parsed.meta.limit,
                remaining: parsed.meta.remaining,
                unlimited: parsed.meta.unlimited || false
              }));
              // One-time soft signup nudge for guests after N messages
              if (
                !user &&
                parsed.meta.messages_used >= NUDGE_AFTER_MESSAGES &&
                !localStorage.getItem(NUDGE_STORAGE_KEY)
              ) {
                setShowSignupNudge(true);
              }
            } else if (parsed.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  role: 'assistant',
                  content: "Sorry, I hit a little snag. Please try again in a moment! 🐢"
                };
                return copy;
              });
            }
          } catch {
            /* ignore malformed sse chunks */
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: 'assistant',
          content: "Sorry, I couldn't reach the tutor server. Please check your connection and try again!"
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [
    'What is the difference between "affect" and "effect"?',
    'Give me a synonym for "difficult"',
    'How do I upgrade my subscription?'
  ];

  return (
    <div className="prof-scaly-chat-root no-print">
      {/* Floating trigger button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            data-testid="prof-scaly-chat-open"
            className="fixed z-40 rounded-full shadow-xl overflow-hidden border-4 border-white flex items-center justify-center"
            style={{
              bottom: 'calc(1rem + 56px)',
              right: '1rem',
              width: 68,
              height: 68,
              backgroundColor: '#10B981'
            }}
            aria-label="Chat with Prof.Scaly"
          >
            <img src={MASCOT_URL} alt="Prof.Scaly" className="w-14 h-14 object-contain" />
            <span
              className="absolute -top-1 -right-1 bg-amber-400 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full border-2 border-white flex items-center gap-0.5"
              style={{ fontFamily: "'Fredoka', system-ui, sans-serif" }}
            >
              <Sparkles size={10} /> AI
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed z-50 bg-white shadow-2xl flex flex-col overflow-hidden border-2 border-emerald-200"
            style={{
              bottom: 'calc(1rem + 56px)',
              right: '1rem',
              width: 'min(400px, calc(100vw - 2rem))',
              height: 'min(600px, calc(100vh - 8rem))',
              borderRadius: '24px'
            }}
            data-testid="prof-scaly-chat-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4 bg-emerald-600 text-white">
              <img src={MASCOT_URL} alt="Prof.Scaly" className="w-10 h-10 rounded-full bg-white p-1 object-contain" />
              <div className="flex-1">
                <div className="font-heading font-bold text-base leading-tight">Prof.Scaly</div>
                <div className="text-xs opacity-90">Your PSLE English & app help tutor</div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 rounded-full p-1.5 transition-colors"
                data-testid="prof-scaly-chat-close"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="prof-scaly-messages">
              {messages.length === 0 && (
                <div className="text-center py-4">
                  <img src={MASCOT_URL} alt="Prof.Scaly" className="w-16 h-16 mx-auto mb-2 object-contain" />
                  <p className="text-slate-700 font-semibold text-sm mb-1">
                    Hi there! I&apos;m Prof.Scaly 🐢
                  </p>
                  <p className="text-slate-500 text-xs mb-4">
                    Ask me anything about PSLE English or how the app works.
                  </p>
                  <div className="space-y-1.5">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(s)}
                        data-testid={`prof-scaly-suggestion-${i}`}
                        className="block w-full text-left text-xs bg-white border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-full px-3 py-1.5 text-emerald-700 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-emerald-600 text-white rounded-br-md'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                    }`}
                  >
                    {msg.content ? (
                      renderContent(msg.content)
                    ) : streaming && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 items-center">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
              {/* Soft signup nudge after 15 messages (guests only, dismissible, once) */}
              {showSignupNudge && !user && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                  data-testid="prof-scaly-signup-nudge"
                >
                  <div className="max-w-[95%] bg-gradient-to-br from-amber-50 to-emerald-50 border-2 border-amber-200 rounded-2xl p-3.5 shadow-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-lg" aria-hidden="true">🌟</span>
                      <div className="flex-1">
                        <div className="text-sm font-heading font-bold text-slate-900 mb-0.5">
                          Enjoying Prof.Scaly?
                        </div>
                        <p className="text-xs text-slate-600 leading-relaxed mb-2">
                          Sign up free to save your progress, unlock daily streaks, and get 50 free practice questions per topic.
                        </p>
                        <div className="flex gap-2 items-center">
                          <a
                            href="/register"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold py-1.5 px-3 rounded-full transition-colors"
                            data-testid="prof-scaly-nudge-cta"
                          >
                            Create free account
                          </a>
                          <button
                            onClick={() => {
                              localStorage.setItem(NUDGE_STORAGE_KEY, '1');
                              setShowSignupNudge(false);
                            }}
                            className="text-xs text-slate-500 hover:text-slate-700 font-semibold"
                            data-testid="prof-scaly-nudge-dismiss"
                          >
                            Not now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-slate-200 bg-white">
              {limitReached && !user ? (
                <div className="flex flex-col gap-2 text-center py-2" data-testid="prof-scaly-limit-banner">
                  <p className="text-sm text-slate-700 font-semibold">
                    You&apos;ve used all {quota?.limit || 5} free chat messages.
                  </p>
                  <a
                    href="/register"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-2 px-4 rounded-full transition-colors"
                    data-testid="prof-scaly-signup-cta"
                  >
                    Sign up free to keep chatting →
                  </a>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendMessage();
                  }}
                  className="flex gap-2 items-end"
                >
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={streaming}
                    placeholder="Ask about PSLE English or the app..."
                    rows={1}
                    className="flex-1 resize-none rounded-2xl border-2 border-slate-200 focus:border-emerald-400 focus:outline-none px-3 py-2 text-sm max-h-24"
                    data-testid="prof-scaly-input"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || streaming}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white p-2.5 rounded-full transition-colors flex-shrink-0"
                    data-testid="prof-scaly-send"
                    aria-label="Send message"
                  >
                    <Send size={16} />
                  </button>
                </form>
              )}
              <p className="text-[10px] text-slate-400 mt-1.5 text-center flex items-center justify-center gap-1">
                <MessageCircle size={10} /> PSLE English & app help only
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfScalyChatBubble;
